import type { MutableRefObject } from "react";
import { useEffect, useRef } from "react";
import type { OrdinanceData, ResolutionData } from "@repo/types";
import { apiService } from "@/services/apiService";
import type {
  LegislationNumberField,
  ReservedLegislationNumber,
} from "./types";

export const LegislationNumberReservation = ({
  tenantId,
  uploadType,
  ordinanceData,
  resolutionData,
  reservedNumberRef,
  queueOperation,
  onReservedNumber,
  setFieldValue,
}: {
  tenantId: string;
  uploadType?: string;
  ordinanceData: OrdinanceData | undefined;
  resolutionData: ResolutionData | undefined;
  reservedNumberRef: MutableRefObject<ReservedLegislationNumber | null>;
  queueOperation: (operation: () => Promise<void>) => Promise<void>;
  onReservedNumber: (field: LegislationNumberField, number: string) => void;
  setFieldValue: (field: string, value: unknown) => void;
}) => {
  const latestFormRef = useRef({
    ordinanceData,
    resolutionData,
    setFieldValue,
  });
  const preservedNumberRef = useRef<
    Partial<Record<LegislationNumberField, string>>
  >({});
  const requestIdRef = useRef(0);
  latestFormRef.current = { ordinanceData, resolutionData, setFieldValue };

  const ordinanceStatus = ordinanceData?.status ?? "proposed";

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    let cancelled = false;

    const releaseReservedNumber = async (
      reservedNumber: ReservedLegislationNumber | null
    ) => {
      if (!reservedNumber || reservedNumber.committed) return;

      if (reservedNumberRef.current === reservedNumber) {
        reservedNumberRef.current = null;
      }

      await apiService.v1.revertLegislationNumber(
        tenantId,
        reservedNumber.type,
        {
          number: reservedNumber.number,
        }
      );
    };

    const getFieldValue = (field: LegislationNumberField) => {
      if (field === "resolutionNumber") {
        return latestFormRef.current.resolutionData?.number ?? "";
      }

      if (field === "ordinanceNumber") {
        return latestFormRef.current.ordinanceData?.number ?? "";
      }

      return latestFormRef.current.ordinanceData?.draftNumber ?? "";
    };

    const preserveFieldValue = (
      field: LegislationNumberField,
      reservedNumber: ReservedLegislationNumber | null
    ) => {
      const value = getFieldValue(field);
      if (!value) return;

      const isReservedSessionNumber =
        reservedNumber?.field === field && reservedNumber.number === value;
      if (isReservedSessionNumber) return;

      preservedNumberRef.current[field] = value;
    };

    const restorePreservedNumber = (field: LegislationNumberField) => {
      const number = preservedNumberRef.current[field];
      if (!number) return false;

      onReservedNumber(field, number);

      if (field === "resolutionNumber") {
        latestFormRef.current.setFieldValue("resolutionData", {
          ...latestFormRef.current.resolutionData,
          number,
          status: latestFormRef.current.resolutionData?.status ?? "proposed",
        } satisfies ResolutionData);
        return true;
      }

      const nextOrdinanceData: OrdinanceData = {
        ...latestFormRef.current.ordinanceData,
        [field === "ordinanceNumber" ? "number" : "draftNumber"]: number,
        status: ordinanceStatus,
      };

      if (field === "ordinanceNumber") {
        nextOrdinanceData.draftNumber = undefined;
      } else {
        nextOrdinanceData.number = undefined;
      }

      latestFormRef.current.setFieldValue("ordinanceData", nextOrdinanceData);
      return true;
    };

    const reserveNumber = async () => {
      if (uploadType !== "ordinances" && uploadType !== "resolutions") {
        await releaseReservedNumber(reservedNumberRef.current);
        return;
      }

      const target =
        uploadType === "resolutions"
          ? ({ type: "resolution", field: "resolutionNumber" } as const)
          : ordinanceStatus === "passed"
            ? ({ type: "ordinance", field: "ordinanceNumber" } as const)
            : ({ type: "bill", field: "draftNumber" } as const);

      const reservedNumber = reservedNumberRef.current;
      if (
        reservedNumber?.type === target.type &&
        reservedNumber.field === target.field
      ) {
        return;
      }

      if (target.field === "ordinanceNumber") {
        preserveFieldValue("draftNumber", reservedNumber);
      } else if (target.field === "draftNumber") {
        preserveFieldValue("ordinanceNumber", reservedNumber);
      }

      await releaseReservedNumber(reservedNumber);
      if (cancelled || requestIdRef.current !== requestId) return;

      const currentTargetValue = getFieldValue(target.field);
      if (currentTargetValue) {
        onReservedNumber(target.field, currentTargetValue);
        if (target.field === "resolutionNumber") {
          latestFormRef.current.setFieldValue("resolutionData", {
            ...latestFormRef.current.resolutionData,
            number: currentTargetValue,
            status: latestFormRef.current.resolutionData?.status ?? "proposed",
          } satisfies ResolutionData);
        } else {
          const nextOrdinanceData: OrdinanceData = {
            ...latestFormRef.current.ordinanceData,
            [target.field === "ordinanceNumber" ? "number" : "draftNumber"]:
              currentTargetValue,
            status: ordinanceStatus,
          };

          if (target.field === "ordinanceNumber") {
            nextOrdinanceData.draftNumber = undefined;
          } else {
            nextOrdinanceData.number = undefined;
          }

          latestFormRef.current.setFieldValue(
            "ordinanceData",
            nextOrdinanceData
          );
        }
        return;
      }

      if (restorePreservedNumber(target.field)) {
        return;
      }

      const { data: number } = await apiService.v1.getNextLegislationNumber(
        tenantId,
        target.type
      );

      if (cancelled || requestIdRef.current !== requestId) {
        await apiService.v1.revertLegislationNumber(tenantId, target.type, {
          number,
        });
        return;
      }

      reservedNumberRef.current = {
        type: target.type,
        field: target.field,
        number,
        committed: false,
      };
      onReservedNumber(target.field, number);

      if (target.field === "resolutionNumber") {
        latestFormRef.current.setFieldValue("resolutionData", {
          ...latestFormRef.current.resolutionData,
          number,
          status: latestFormRef.current.resolutionData?.status ?? "proposed",
        } satisfies ResolutionData);
        return;
      }

      const nextOrdinanceData: OrdinanceData = {
        ...latestFormRef.current.ordinanceData,
        [target.field === "ordinanceNumber" ? "number" : "draftNumber"]: number,
        status: ordinanceStatus,
      };

      if (target.field === "ordinanceNumber") {
        nextOrdinanceData.draftNumber = undefined;
      } else {
        nextOrdinanceData.number = undefined;
      }

      latestFormRef.current.setFieldValue("ordinanceData", nextOrdinanceData);
    };

    void queueOperation(reserveNumber);

    return () => {
      cancelled = true;
    };
  }, [
    onReservedNumber,
    ordinanceStatus,
    queueOperation,
    reservedNumberRef,
    tenantId,
    uploadType,
  ]);

  useEffect(() => {
    return () => {
      const reservedNumber = reservedNumberRef.current;
      if (!reservedNumber || reservedNumber.committed) return;

      reservedNumberRef.current = null;
      void queueOperation(async () => {
        await apiService.v1.revertLegislationNumber(
          tenantId,
          reservedNumber.type,
          {
            number: reservedNumber.number,
          }
        );
      });
    };
  }, [queueOperation, reservedNumberRef, tenantId]);

  return null;
};
